<template>
  <page-view
    title="Play Site"
    :class="`${featuresEnabled.includes('ppv') && (featuresEnabled.includes('lvs') && lvsEditorAccessable) ? 'subpage' : ''} play-site`"
    icon="shopping-music"
    :largeMaxWidth="true"
    :contentLoading="!done && !config"
    :backLink="featuresEnabled.includes('ppv') && (featuresEnabled.includes('lvs') && lvsEditorAccessable) ? '/appearance' : ''"
    :backText="featuresEnabled.includes('ppv') && (featuresEnabled.includes('lvs') && lvsEditorAccessable) ? 'Back to Appearance ': ''"
  >
    <template v-slot:header-right>
      <div class="ta-r">
        <div class="d-f jc-fe">
          <Button @click="setPlayConfig" class="small minimal-border-radius only-text blue keep-hover m-r-2" v-if="updateable">Disregard changes</Button>
          <Button @click="update" :loading="updating" :disabled="!updateable">Update Play</Button>
        </div>
        <label class="dark normal m-t-1" v-if="updateable"><span>It may take a little while for the Play Site to update.</span></label>
      </div>
    </template>

    <div class="m-b-4 ta-c">
      <p>
        <span class="opacity-50">{{ $t('site.access_playsite') }}&nbsp;</span>
        <a :href="playsiteUrl" target="_blank" class="link blue opacity-75">{{ playsiteUrl }}</a>
      </p>
    </div>

    <div class="d-f fd-c fd-md-r f-g" v-if="config" :key="playkey">
      <div class="f-2">
        <card-minimal title="Title and Logotype">
          <div class="p-a-4">
            <p class="card-content-title">
              <strong>Title</strong>
              <span>The title will appear in the header if the logo is not set</span>
            </p>
            <FormInput type="text" class="f-w" v-model="config.metadata.title" />
            <p class="card-content-title m-t-2">
              <strong>Logotype</strong>
              <span>The logotype will shown in the page header. It will override the title if set</span>
            </p>
            <Fileupload
              :uploadText="$t('fileupload.click_or_drag_logotype')"
              :maxSize=.4
              :validFormats="['image/jpeg', 'image/png']"
              :uploadCriteria="$t('fileupload.logotype_criteria')"
              :existingFile="logotypeURL"
              :state="logotype.state"
              @filedata="setLogotype"
            />
          </div>
        </card-minimal>
        <card-minimal title="Information" subtitle="Will be shown at the bottom of your Play Site" class="m-t-4">
          <div class="p-a-4">
            <p class="card-content-title">
              <strong>Website URL</strong>
              <span>The URL to your website</span>
            </p>
            <FormInput type="text" class="f-w" v-model="config.metadata.website_url" />
            <p class="card-content-title m-t-2">
              <strong>Email</strong>
              <span>Email address you can be contacted with</span>
            </p>
            <FormInput type="text" class="f-w" v-model="config.metadata.email" />
            <p class="card-content-title m-t-2">
              <strong>Phone</strong>
              <span>Phone number you can be contacted with</span>
            </p>
            <FormInput type="text" class="f-w" v-model="config.metadata.phone" />
            <p class="card-content-title m-t-2">
              <strong>Footertext</strong>
              <span>Other information you want to be displayed in the footer</span>
            </p>
            <form-textarea class="small" :toolbar="['bold', 'ul', 'link']" height="185" v-model="config.metadata.footer" />
          </div>
        </card-minimal>
      </div>
      <div class="f-1">
        <card-minimal title="Broadcasts" class="m-t-1 m-t-md-0">
          <div class="p-a-4">
            <p class="card-content-title">
              <strong>Default poster</strong>
              <span>When you dont have a poster set on your broadcast this will be used instead.</span>
            </p>
            <media-upload
              :legacy="true"
              @images="setPoster"
              :unsplash="true"
              :multiple="false"
              title="Poster image"
              :overwrite="true"
              :data="{
                force: true,
                images: defaultPosterURL ? [{ value: defaultPosterURL }] : []
              }"
            />
          </div>
        </card-minimal>
        <!--<card-minimal title="Information Message" subtitle="Here you can broadcast to your viewers of any disturbances etc." class="m-t-4">
          <div class="p-x-4 p-t-1 p-b-4">
            <form-textarea class="small" :toolbar="['bold', 'ul', 'link']" height="185" v-model="config.information_message.message" />
          </div>
        </card-minimal>-->
        <card-minimal title="Collections" subtitle="You can change how collections are viewed and named in public" class="m-t-4">
          <div class="p-t-1 p-x-4 p-b-4">
            <div class="d-f f-g fd-r">
              <div class="f-1">
                <p class="card-content-title ta-c">
                  <strong>Singular</strong>
                </p>
                <p class="card-content-title">
                  <strong>Swedish</strong>
                </p>
                <FormInput type="text" class="f-w" v-model="config.collections.name.singular.sv" />
                <FormInput type="text" class="f-w m-t-2" v-model="config.collections.name.plural.sv" />
              </div>
              <div class="f-1">
                <p class="card-content-title ta-c">
                  <strong>Plural</strong>
                </p>
                <p class="card-content-title">
                  <strong>English</strong>
                </p>
                <FormInput type="text" class="f-w" v-model="config.collections.name.singular.en" />
                <FormInput type="text" class="f-w m-t-2" v-model="config.collections.name.plural.en" />
              </div>
            </div>

            <Toggle
              :text="$t(`library.${collection}`)"
              @change="config.collections.default[collection].disabled = !$event"
              :value="!config.collections.default[collection].disabled"
              v-for="(value, collection) in config.collections.default"
              :key="collection"
              class="m-t-2"
            />
          </div>
        </card-minimal>
        <card-minimal title="Support" class="m-t-4">
          <div class="p-a-4">
            <Toggle
              text="Help Button"
              explanation="Include our support button"
              @change="config.support.help_button.disabled = !$event"
              :value="!config.support.help_button.disabled"
            />
          </div>
        </card-minimal>
        <card-minimal-title title="Custom CSS" class="m-t-4">
          <template v-slot:right-side>
            <a class="link blue" @click="showCss = !showCss">{{ showCss ? 'Hide' : 'Show' }}</a>
          </template>
        </card-minimal-title>
        <card-minimal subtitle="Add custom CSS to your Play Site. Use with caution." v-if="showCss">
          <div class="p-x-4 p-b-4 p-t-1">
            <textarea v-model="config.player.display.custom_css" />
          </div>
        </card-minimal>
      </div>
    </div>
  </page-view>
</template>

<script>

import Fileupload from '@/components/Fileupload.vue';
import MediaUpload from '@/components/MediaUpload.vue';
import Toggle from '@/components/toggle.vue';

export default {
  components: {
    Fileupload,
    MediaUpload,
    Toggle,
  },
  computed: {
    playsiteUrl() {
      if (!this.account) return '';
      const [fqdn] = this.account.fqdn.split('.');
      return `https://${fqdn}.streamify.io/play`;
    },
    updateable() {
      if (this.logotype.state === 'updateable') {
        return true;
      }

      if (this.defaultPoster.state === 'updateable') {
        return true;
      }

      if (this.playConfigChanged) {
        return true;
      }

      return false;
    },
    /**
     * Return the current playconfig
     * @returns {Object}
     */
    playConfig() {
      return this.$store.getters['Play/config'];
    },
    playConfigChanged() {
      return JSON.stringify(this.playConfig) !== JSON.stringify(this.config);
    },
    /**
     * Return boolean if lvs editor is accessable
     * @returns {Boolean}
     */
    lvsEditorAccessable() {
      const trialEnded = this.$store.getters['AccountManager/trialEnded'];
      const subscriptionEnded = this.$store.getters['AccountManager/subscriptionEnded'];

      if (trialEnded || subscriptionEnded) {
        return false;
      }

      return this.accountSettings?.shopping?.permissions?.appearance;
    },
    logotypeURL() {
      if (!this.account) return null;
      const [fqdn] = this.account.fqdn.split('.');
      return this.config.images.logotype ? `https://${fqdn}.streamify.io/resources/${this.config.images.logotype}` : null;
    },
    defaultPosterURL() {
      if (!this.account) return null;
      if (this.config.resources) {
        const [poster] = this.config.resources.filter(item => (typeof item.name !== 'undefined' && item.name.indexOf('640x360') >= 0 ? item : null));
        const [fqdn] = this.account.fqdn.split('.');
        return poster ? `https://${fqdn}.streamify.io/resources/${poster.name}` : null;
      }
      return null;
    },
  },
  methods: {
    /**
     * Set the playconfig from store
     * @returns {void}
     */
    setPlayConfig() {
      this.config = JSON.parse(JSON.stringify(this.playConfig));
      this.logotype.state = '';
      this.defaultPoster.state = '';
      this.playkey += 1;

      // Fix for collection names
      if (this.config.collections.name.plural === null) {
        this.config.collections.name.plural = { en: '', sv: '' };
      }
      if (this.config.collections.name.singular === null) {
        this.config.collections.name.singular = { en: '', sv: '' };
      }
    },
    /**
     * Set logotype for updating
     * @param {String} file
     */
    setLogotype(file) {
      this.logotype.image = file || null;
      this.logotype.state = 'updateable';
    },
    /**
     * Set poster for updating
     * @param {FormData} poster
     */
    setPoster(poster) {
      this.defaultPoster.image = poster || null;
      this.defaultPoster.state = 'updateable';
    },
    /**
     * Update playsettings
     * @returns {void}
     */
    async update() {
      if (this.updating || !this.updateable) {
        return;
      }

      const {
        config: {
          metadata: {
            title,
            website_url,
            email,
            footer,
            phone,
          },
          support,
          player,
          // information_message,
          collections,
        },
        logotype: {
          image: logo,
          state: logoState,
        },
        defaultPoster: {
          image: poster,
          state: posterState,
        },
      } = this;

      this.updating = true;

      if (logoState === 'updateable') {
        await this.$store.dispatch('Play/updateLogo', logo);
        this.logotype.state = '';
      }

      if (posterState === 'updateable') {
        await this.$store.dispatch('Play/updateDefaultPoster', poster);
        this.defaultPoster.state = '';
      }

      await this.$store.dispatch('Play/updateConfig', {
        title,
        website_url,
        email,
        phone,
        footer,
        collections,
        // information_message,
        player,
        support,
      });

      await this.$store.dispatch('Play/getConfig');
      this.updating = false;
    },
  },
  watch: {
    playConfig() {
      this.setPlayConfig();
    },
  },
  data() {
    return {
      done: false,
      updating: false,
      config: null,
      logotype: {
        image: null,
        state: '',
      },
      defaultPoster: {
        image: null,
        state: '',
      },
      showCss: false,
      playkey: 0,
    };
  },
  async mounted() {
    await this.$store.dispatch('Play/getConfig');

    this.$nextTick(() => {
      this.done = true;
    });
  },
};
</script>

<style lang="scss">
.play-site {
  .media-upload {
    .card-minimal {
      .card-title {
        display: none;
      }
      .card-content {
        border-radius: 0;
        box-shadow: unset;

        & > .p-a-3 {
          padding: 0;
        }
      }
    }
    .image-gallery {
      .images {
        background-position: 50% 50%;
        background-size: cover;
      }
    }
    .upload-area:after, .image-gallery .images {
      padding-bottom: 56.6%;
      padding-top: 0;
    }
  }
}
</style>
